import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, alpha, Chip, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircleIcon from '@mui/icons-material/Circle';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useAuth } from '../components/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabaseClient';
import { Unidade } from '../services/types';
import { Grid } from '@mui/material'; // Ensure Grid is imported

// --- Quiet Luxury Palette ---
const PALETTE = {
    DEEP_TEAL: '#0F2529',
    STONE_GREY: '#8B8C89',
    SOFT_CREAM: '#F5F2EA',
    AGED_BRONZE: '#CD7F32',
    WHITE: '#FFFFFF',
    BLACK: '#000000'
};

// --- Fonts ---
// Injected via style tag in component for simplicity, but ideally linked in HTML
const FONTS = {
    SERIF: '"Playfair Display", "Times New Roman", serif',
    SANS: '"Inter", "Helvetica", "Arial", sans-serif'
};

type ViewState = 'HERO' | 'UNITS';

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, modulosAcessiveis, loading } = useAuth();
    const [currentView, setCurrentView] = useState<ViewState>('HERO');
    const [isTransitioning, setIsTransitioning] = useState(false);

    // --- Font Injection ---
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        return () => { document.head.removeChild(link); };
    }, []);

    // --- Domain Protection ---
    useEffect(() => {
        if (window.location.hostname === 'assinaturas-sistema.com.br') {
            navigate('/404', { replace: true });
        }
    }, [navigate]);

    // --- Authentication Logic ---
    useEffect(() => {
        if (user && !loading && modulosAcessiveis) {
            const modulePaths: { [key: string]: string } = {
                'Obras': '/obras', 'Unidades': '/unidades', 'Administração': '/admin',
                'Lançamentos': '/lancamentos', 'Fornecedores': '/fornecedores', 'Investidores': '/investidores',
                'Categorias': '/categorias', 'Fases da Obra': '/fases-de-obra', 'Itens': '/itens',
                'Aportes e Resgates': '/aportes-resgates'
            };
            
            let path = '/dashboard';
            // Prioritize Obras as the initial page
            if (modulosAcessiveis.includes('Obras')) {
                path = '/obras';
            } else {
                const foundModule = modulosAcessiveis.find(m => modulePaths[m]);
                if (foundModule) {
                    path = modulePaths[foundModule];
                }
            }
            navigate(path);
        }
    }, [user, loading, modulosAcessiveis, navigate]);

    // --- Transition Handler ---
    const handleViewChange = (view: ViewState) => {
        if (currentView === view) return;
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentView(view);
            setIsTransitioning(false);
        }, 500); // Wait for fade out
    };

    return (
        <Box sx={{
            height: '100vh',
            width: '100vw',
            bgcolor: PALETTE.SOFT_CREAM,
            overflow: 'hidden',
            position: 'relative',
            color: PALETTE.DEEP_TEAL,
            fontFamily: FONTS.SANS
        }}>
            {/* --- Navigation Overlay --- */}
            <NavOverlay currentView={currentView} onViewChange={handleViewChange} onLogin={() => navigate('/login')} />

            {/* --- Content Stage --- */}
            <Box sx={{
                height: '100%',
                width: '100%',
                opacity: isTransitioning ? 0 : 1,
                transform: isTransitioning ? 'translateY(20px)' : 'translateY(0)',
                transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-out'
            }}>
                {currentView === 'HERO' && <HeroView onViewChange={handleViewChange} />}
                {currentView === 'UNITS' && <UnitsView />}
            </Box>
        </Box>
    );
};

// --- Components ---

const NavOverlay: React.FC<{ currentView: ViewState, onViewChange: (v: ViewState) => void, onLogin: () => void }> = ({ currentView, onViewChange, onLogin }) => (
    <Box sx={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        p: { xs: 3, md: 5 },
        zIndex: 100,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    }}>
        {/* Brand */}
        <Box>
            <Typography variant="h5" sx={{
                fontFamily: FONTS.SERIF,
                fontWeight: 700,
                letterSpacing: 2,
                color: PALETTE.DEEP_TEAL
            }}>
                CRX
            </Typography>
            <Typography variant="caption" sx={{
                display: 'block',
                letterSpacing: 4,
                fontSize: '0.6rem',
                mt: 0.5,
                color: alpha(PALETTE.DEEP_TEAL, 0.7)
            }}>
                EMPREENDIMENTOS
            </Typography>
        </Box>

        {/* Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, md: 4 }, pointerEvents: 'auto' }}>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <NavButton label="Início" active={currentView === 'HERO'} onClick={() => onViewChange('HERO')} />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <NavButton label="Coleção" active={currentView === 'UNITS'} onClick={() => onViewChange('UNITS')} />
            </Box>
            <Button
                variant="outlined"
                onClick={onLogin}
                sx={{
                    borderColor: alpha(PALETTE.DEEP_TEAL, 0.3),
                    color: PALETTE.DEEP_TEAL,
                    borderRadius: 0,
                    px: 3,
                    fontFamily: FONTS.SANS,
                    letterSpacing: 1,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    '&:hover': { borderColor: PALETTE.DEEP_TEAL, bgcolor: 'transparent' }
                }}
            >
                Portal
            </Button>
        </Box>
    </Box>
);

const NavButton: React.FC<{ label: string, active: boolean, onClick: () => void }> = ({ label, active, onClick }) => (
    <Typography
        onClick={onClick}
        sx={{
            cursor: 'pointer',
            fontFamily: FONTS.SANS,
            fontSize: '0.8rem',
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: active ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.4),
            borderBottom: active ? `1px solid ${PALETTE.DEEP_TEAL}` : '1px solid transparent',
            pb: 0.5,
            transition: 'all 0.3s',
            '&:hover': { color: PALETTE.DEEP_TEAL }
        }}
    >
        {label}
    </Typography>
);

const HeroView: React.FC<{ onViewChange: (v: ViewState) => void }> = ({ onViewChange }) => (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="xl">
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'center', gap: { xs: 5, md: 10 } }}>
                {/* Text Content */}
                <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
                    <Typography variant="caption" sx={{
                        display: 'block',
                        color: PALETTE.AGED_BRONZE,
                        letterSpacing: 3,
                        mb: 2,
                        fontWeight: 600,
                        textTransform: 'uppercase'
                    }}>
                        Quiet Living
                    </Typography>
                    <Typography variant="h1" sx={{
                        fontFamily: FONTS.SERIF,
                        fontSize: { xs: '3rem', md: '5.5rem' },
                        lineHeight: 1.1,
                        color: PALETTE.DEEP_TEAL,
                        mb: 4
                    }}>
                        A arte <br />
                        <Box component="span" sx={{ fontStyle: 'italic', fontWeight: 400 }}>de viver bem.</Box>
                    </Typography>
                    <Typography sx={{
                        color: PALETTE.STONE_GREY,
                        maxWidth: 400,
                        mb: 6,
                        lineHeight: 1.8,
                        fontSize: '1.1rem'
                    }}>
                        Mais que morar, pertencer. Arquitetura que inspira, conforto que acolhe.
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', group: 'hover' }} onClick={() => onViewChange('UNITS')}>
                        <Box sx={{
                            width: 50, height: 50,
                            borderRadius: '50%',
                            border: `1px solid ${alpha(PALETTE.DEEP_TEAL, 0.2)}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.3s',
                            '&:hover': { borderColor: PALETTE.DEEP_TEAL, bgcolor: PALETTE.DEEP_TEAL, color: PALETTE.WHITE }
                        }}>
                            <ArrowForwardIcon fontSize="small" />
                        </Box>
                        <Typography sx={{ letterSpacing: 2, fontSize: '0.8rem', textTransform: 'uppercase' }}>Explorar Unidades</Typography>
                    </Box>
                </Box>

                {/* Hero Image / Abstract */}
                <Box sx={{ flex: 1.2, height: { xs: '40vh', md: '70vh' }, width: '100%', position: 'relative' }}>
                    <Box sx={{
                        position: 'absolute',
                        top: 0, right: 0, bottom: 0, left: 0,
                        bgcolor: PALETTE.DEEP_TEAL,
                        clipPath: 'polygon(10% 0, 100% 0, 100% 100%, 0% 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden'
                    }}>
                        {/* Placeholder for cinematic image - using CSS gradient for "atmospheric" feel if image missing */}
                        <Box sx={{
                            width: '100%', height: '100%',
                            background: `linear-gradient(45deg, ${PALETTE.DEEP_TEAL}, #1a3a40)`,
                            opacity: 0.8
                        }} />
                        {/* Ideally an <img> here */}
                        <Typography variant="h2" sx={{
                            position: 'absolute',
                            color: alpha(PALETTE.WHITE, 0.05),
                            fontFamily: FONTS.SERIF,
                            fontSize: '10rem',
                            fontStyle: 'italic',
                            zIndex: 0
                        }}>
                            CRX
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Container>
    </Box>
);

const UnitsView: React.FC = () => {
    const { data: unidades } = useQuery<Unidade[]>({
        queryKey: ['public_unidades'],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_unidades_disponiveis');
            if (error) throw error;
            return data || [];
        }
    });

    const [viewMode, setViewMode] = useState<'carousel' | 'grid' | 'list'>('carousel');
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 400;
            scrollContainerRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
        }
    };

    return (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start', // Always top align to keep header fixed
            pt: { xs: 15, md: 15 }, // clearance for header
            pl: { xs: 2, md: 10 },
            pr: { xs: 2, md: 10 } // Add padding right for grid view
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'flex-end' },
                mb: 4,
                pr: { xs: 2, md: 0 },
                gap: { xs: 2, md: 0 }
            }}>
                <Box>
                    <Typography variant="overline" sx={{ color: PALETTE.AGED_BRONZE, letterSpacing: 3, mb: 1, fontWeight: 600 }}>
                        Coleção Exclusiva
                    </Typography>
                    <Typography variant="h3" sx={{
                        fontFamily: FONTS.SERIF,
                        color: PALETTE.DEEP_TEAL,
                        fontSize: { xs: '2.5rem', md: '3rem' }
                    }}>
                        Imóveis Disponíveis
                    </Typography>
                </Box>

                {/* View Toggle */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        onClick={() => setViewMode('carousel')}
                        sx={{
                            color: viewMode === 'carousel' ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.3),
                            border: `1px solid ${viewMode === 'carousel' ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.1)}`
                        }}
                    >
                        <ViewCarouselIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => setViewMode('grid')}
                        sx={{
                            color: viewMode === 'grid' ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.3),
                            border: `1px solid ${viewMode === 'grid' ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.1)}`
                        }}
                    >
                        <GridViewIcon />
                    </IconButton>
                    <IconButton
                        onClick={() => setViewMode('list')}
                        sx={{
                            color: viewMode === 'list' ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.3),
                            border: `1px solid ${viewMode === 'list' ? PALETTE.DEEP_TEAL : alpha(PALETTE.DEEP_TEAL, 0.1)}`
                        }}
                    >
                        <ViewListIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* --- Carousel View --- */}
            {viewMode === 'carousel' && (
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    overflowY: { xs: 'auto', md: 'visible' }, // Scroll only on mobile
                    maxHeight: { xs: '75vh', md: 'unset' }, // Limit height only on mobile
                    pb: 4 // Extra padding for bottom content
                }}>
                    {/* Left Button */}
                    <IconButton
                        onClick={() => scroll('left')}
                        sx={{
                            position: 'absolute',
                            left: { xs: 0, md: -20 }, // Adjusted to be closer
                            top: '200px', // Center of the 400px image
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            border: `1px solid ${alpha(PALETTE.DEEP_TEAL, 0.2)}`,
                            bgcolor: PALETTE.SOFT_CREAM, // solid bg to cover content if needed
                            display: { xs: 'none', md: 'flex' }, // Hide on mobile if swipe is preferred, or adjust
                            '&:hover': { bgcolor: PALETTE.DEEP_TEAL, color: PALETTE.WHITE }
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            display: 'flex',
                            gap: 4,
                            overflowX: 'auto',
                            pb: 4,
                            // pr: 10, removed to keep consistent spacing
                            width: '100%',
                            '::-webkit-scrollbar': { display: 'none' }, // Hide scrollbar
                            scrollbarWidth: 'none',
                            scrollBehavior: 'smooth'
                        }}
                    >
                        {unidades?.map((unidade) => (
                            <Box key={unidade.id} sx={{ minWidth: { xs: 280, md: 350 }, cursor: 'pointer', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-10px)' } }}>
                                <Box sx={{
                                    height: 400,
                                    bgcolor: '#E0E0E0',
                                    position: 'relative',
                                    mb: 2,
                                    overflow: 'hidden'
                                }}>
                                    {/* Image Placeholder */}
                                    {unidade.imagens && unidade.imagens.length > 0 ? (
                                        <img src={unidade.imagens[0]} alt={unidade.identificacao} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <Box sx={{ width: '100%', height: '100%', bgcolor: PALETTE.DEEP_TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', color: alpha(PALETTE.WHITE, 0.2) }}>
                                            <CircleIcon sx={{ fontSize: 40 }} />
                                        </Box>
                                    )}
                                    <Chip
                                        label="DISPONÍVEL"
                                        sx={{
                                            position: 'absolute', top: 16, right: 16,
                                            bgcolor: PALETTE.WHITE, color: PALETTE.DEEP_TEAL,
                                            borderRadius: 0, fontWeight: 600, fontSize: '0.7rem'
                                        }}
                                    />
                                </Box>
                                <Typography variant="h5" sx={{ fontFamily: FONTS.SERIF, color: PALETTE.DEEP_TEAL }}>
                                    {unidade.identificacao}
                                </Typography>
                                <Typography variant="body2" sx={{ color: PALETTE.STONE_GREY, mb: 1 }}>
                                    {unidade.endereco} {unidade.bairro && `- ${unidade.bairro}`}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                                    {unidade.quartos && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL, fontWeight: 500 }}>{unidade.quartos} Quartos</Typography>}
                                    {unidade.area && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL, fontWeight: 500 }}>{unidade.area} m²</Typography>}
                                    {unidade.vagas && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL, fontWeight: 500 }}>{unidade.vagas} Vagas</Typography>}
                                </Box>
                                <Typography variant="h6" sx={{ color: PALETTE.AGED_BRONZE, fontWeight: 600 }}>
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(unidade.valor)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* Right Button */}
                    <IconButton
                        onClick={() => scroll('right')}
                        sx={{
                            position: 'absolute',
                            right: { xs: 0, md: -20 }, // Adjusted to be closer
                            top: '200px', // Center of the 400px image
                            transform: 'translateY(-50%)',
                            zIndex: 2,
                            border: `1px solid ${alpha(PALETTE.DEEP_TEAL, 0.2)}`,
                            bgcolor: PALETTE.SOFT_CREAM,
                            display: { xs: 'none', md: 'flex' },
                            '&:hover': { bgcolor: PALETTE.DEEP_TEAL, color: PALETTE.WHITE }
                        }}
                    >
                        <ArrowForwardIcon />
                    </IconButton>
                </Box>
            )}

            {/* --- Grid View --- */}
            {viewMode === 'grid' && (
                <Box sx={{ overflowY: 'auto', maxHeight: '72vh', pr: 2, pb: 10 }}>
                    <Grid container spacing={4}>
                        {unidades?.map((unidade) => (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={unidade.id}>
                                <Box sx={{ cursor: 'pointer', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
                                    <Box sx={{
                                        height: 300,
                                        bgcolor: '#E0E0E0',
                                        position: 'relative',
                                        mb: 2,
                                        overflow: 'hidden'
                                    }}>
                                        {/* Image Placeholder */}
                                        {unidade.imagens && unidade.imagens.length > 0 ? (
                                            <img src={unidade.imagens[0]} alt={unidade.identificacao} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <Box sx={{ width: '100%', height: '100%', bgcolor: PALETTE.DEEP_TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', color: alpha(PALETTE.WHITE, 0.2) }}>
                                                <CircleIcon sx={{ fontSize: 40 }} />
                                            </Box>
                                        )}
                                        <Chip
                                            label="DISPONÍVEL"
                                            sx={{
                                                position: 'absolute', top: 12, right: 12,
                                                bgcolor: PALETTE.WHITE, color: PALETTE.DEEP_TEAL,
                                                borderRadius: 0, fontWeight: 600, fontSize: '0.6rem'
                                            }}
                                        />
                                    </Box>
                                    <Typography variant="h5" sx={{ fontFamily: FONTS.SERIF, color: PALETTE.DEEP_TEAL }}>
                                        {unidade.identificacao}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: PALETTE.STONE_GREY, mb: 0.5 }}>
                                        {unidade.endereco} {unidade.bairro && `- ${unidade.bairro}`}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                                        {unidade.quartos && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL, fontWeight: 500 }}>{unidade.quartos} Quartos</Typography>}
                                        {unidade.area && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL, fontWeight: 500 }}>{unidade.area} m²</Typography>}
                                        {unidade.vagas && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL, fontWeight: 500 }}>{unidade.vagas} Vagas</Typography>}
                                    </Box>
                                    <Typography variant="h6" sx={{ color: PALETTE.AGED_BRONZE, fontWeight: 600 }}>
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(unidade.valor)}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* --- List View --- */}
            {viewMode === 'list' && (
                <Box sx={{ overflowY: 'auto', maxHeight: '72vh', pr: 2, pb: 10 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {unidades?.map((unidade) => (
                            <Box key={unidade.id} sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 3,
                                cursor: 'pointer',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateX(5px)' }
                            }}>
                                <Box sx={{
                                    width: { xs: '100%', sm: 300 },
                                    height: 200,
                                    bgcolor: '#E0E0E0',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    flexShrink: 0
                                }}>
                                    {/* Image Placeholder */}
                                    {unidade.imagens && unidade.imagens.length > 0 ? (
                                        <img src={unidade.imagens[0]} alt={unidade.identificacao} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <Box sx={{ width: '100%', height: '100%', bgcolor: PALETTE.DEEP_TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', color: alpha(PALETTE.WHITE, 0.2) }}>
                                            <CircleIcon sx={{ fontSize: 40 }} />
                                        </Box>
                                    )}
                                    <Chip
                                        label="DISPONÍVEL"
                                        sx={{
                                            position: 'absolute', top: 12, left: 12,
                                            bgcolor: PALETTE.WHITE, color: PALETTE.DEEP_TEAL,
                                            borderRadius: 0, fontWeight: 600, fontSize: '0.6rem'
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Typography variant="h5" sx={{ fontFamily: FONTS.SERIF, color: PALETTE.DEEP_TEAL, mb: 1 }}>
                                        {unidade.identificacao}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: PALETTE.STONE_GREY, mb: 1 }}>
                                        {unidade.endereco} - {unidade.bairro}, {unidade.municipio}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: PALETTE.STONE_GREY, mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {unidade.descricao}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                                        {unidade.quartos && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL }}>{unidade.quartos} Quartos</Typography>}
                                        {unidade.area && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL }}>{unidade.area} m²</Typography>}
                                        {unidade.vagas && <Typography variant="caption" sx={{ color: PALETTE.DEEP_TEAL }}>{unidade.vagas} Vagas</Typography>}
                                    </Box>
                                    <Typography variant="h6" sx={{ color: PALETTE.AGED_BRONZE, fontWeight: 600 }}>
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(unidade.valor)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {!unidades?.length && (
                <Typography sx={{ color: PALETTE.STONE_GREY, mt: 4 }}>Nenhuma unidade disponível no momento.</Typography>
            )}
        </Box>
    );
};

export default LandingPage;
